import { IonApp, IonContent, IonRouterOutlet, setupIonicReact } from "@ionic/react";
import { IonReactRouter }                           from "@ionic/react-router";
import { LoadSettings, ReloadSettings, themes } from "components/AppSettings";
import { DvcPageRoutes }            from "navigation/PageList";
import { LogoutOnlyNavBar, NavBar } from "navigation/NavBar";
import { RouterMap }                from "navigation/RouterMap";
import { Language, DeviceConnectionCTX, RefreshContext, DeviceAlarmCTX } from "functions/Context";
import { LanguageCode }                                  from "functions/Language";
import { ResetEnergyCache }               from "functions/networking/EnergyLoading";
import { DeviceInfo, SupportedDevices, TYPE_GCR, TYPE_GCRT, TYPE_ICR }   from "functions/networking/NetTypes";
import { DeviceConnection }               from "functions/networking/DeviceConnection";
import { useMqttClient, MqttClientState } from "functions/networking/Networking";
import { SequentialLoader, useLoader }               from "functions/networking/useLoader";
import { DevicesPage } from "pages/Devices";
import { LoginPage }   from "Login/Login";
import { FC, PropsWithChildren, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Redirect, Route } from "react-router-dom";

/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";

/* Basic CSS for apps built with Ionic */
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";

/* Optional CSS utils that can be commented out */
import "@ionic/react/css/display.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/padding.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";

/* Theme variables */
import "theme/variables.css";
import "theme/global.css";


setupIonicReact();

function useDeviceConnection() {
	const [deviceConnection, setDeviceConnection] = useState<DeviceConnection<SupportedDevices> | null>(null);

	return [
		deviceConnection,
		(val: typeof deviceConnection) => setDeviceConnection(function UpdateConnection(old) {
			if (old) {
				old.Stop();
				ResetEnergyCache();
			}
			return val;
		}),
	] as const;
}

const AlarmCtxProvider : FC<PropsWithChildren<{
	deviceConnection: DeviceConnection<SupportedDevices>
}>> = (props) => {
	const loader = useLoader("Alarms", null, {
		watchOthers: true,
		disableWhenLoaded: true,
		disabled: !props.deviceConnection.IsFrom([TYPE_GCR || TYPE_GCRT || TYPE_ICR]),
	});
	const oldData = useRef(loader.data?.Data);
	const [showAlarm, setShowAlarm] = useState(false);

	useEffect(() => {
		let newAlarm = false;

		if (oldData.current === undefined) {}
		else if (loader.data?.Data === undefined) {}
		else if (loader.data.Data.length === 0) {}
		else if (oldData.current.length < loader.data.Data.length) {
			newAlarm = true;
		}
		else if (oldData.current.length === loader.data.Data.length) {
			const oldTime = oldData.current[0].time;
			const newTime = loader.data.Data[0].time;

			if (oldTime !== newTime) newAlarm = true;
		}

		if (newAlarm) setShowAlarm(true);

		oldData.current = loader.data?.Data;
	}, [loader.data]);

	return <DeviceAlarmCTX.Provider value={{ showAlarm, setShowAlarm }}>
		{props.children}
	</DeviceAlarmCTX.Provider>
}

const App: FC = () => {
  const [lang, setLang] = useState<LanguageCode>("cs");
  const [, setTheme] = useState<typeof themes[number]>("light");
  const [refresh, setRefresh] = useState(5);

	const [user,   setUser]   = useState<string | null>(null);
	const [pass,   setPass]   = useState<string | null>(null);
	const [broker, setBroker] = useState<string | null>(null);

	const mqtt = useMqttClient(user, pass, broker ?? "");

	const [dvc, setDvc] = useState<DeviceInfo<SupportedDevices> | null>(null);
	const [deviceConnection, setDeviceConnection] = useDeviceConnection();

  useLayoutEffect(() => {
    ReloadSettings.callback = async () => {
      let cfg = await LoadSettings();

      setLang(cfg.language);
      setRefresh(cfg.refresh);

      setTheme(cfg.theme);
      switch (cfg.theme) {
        case 'dark':  document.body.classList.add   ("dark"); break;
        case 'light': document.body.classList.remove("dark"); break;
      }
    }

    ReloadSettings.callback();
  }, []);

	useEffect(function Disconnect() {
		if (null === mqtt.client) {
			setUser(null);
			setPass(null);
			setDvc(null);
		}
	}, [mqtt.client]);

	useEffect(function ResetDevice() {
		if (user === null || null === mqtt.client || mqtt.state !== MqttClientState.ok || dvc === null || mqtt.client.disconnecting) {
			setDeviceConnection(null);
		} else {
			setDeviceConnection(new DeviceConnection(user, mqtt.client, dvc));
		}
	}, [mqtt.client, mqtt.state, user, dvc]);

	const login_callback = useRef<null | ((succ: boolean) => void)>(null);
	useEffect(function CallLoginCallback() {
		if (!login_callback.current) return;
		if (mqtt.state === MqttClientState.connecting) return;

		const success = mqtt.state === MqttClientState.ok;
		login_callback.current(success);
		login_callback.current = null;
	}, [mqtt.state]);

	let content;
	if (user === null || pass === null || mqtt.client === null || mqtt.state !== MqttClientState.ok) {
		content = <LoginPage login={(prof) => {
			setUser(prof.name);
			setPass(prof.pass);
			setBroker(prof.broker);

			if (login_callback.current) login_callback.current(false);
			return new Promise<boolean>(res => login_callback.current = res);
		}} mqttErr={mqtt.error} />;
	} else if (deviceConnection === null) {
		content = <>
			<IonContent>
				<DevicesPage setDevice={setDvc} mqtt={mqtt.client} broadcastTopic={mqtt.broadcastTopic} />
			</IonContent>
			<LogoutOnlyNavBar logout={() => { setUser(null); setPass(null); }} />
		</>
	} else {
		content = <>
			<RefreshContext.Provider value={refresh}>
			<DeviceConnectionCTX.Provider value={deviceConnection}>
			<AlarmCtxProvider deviceConnection={deviceConnection}>
			<SequentialLoader connection={deviceConnection} refresh={refresh} />
			<IonContent>
				<IonRouterOutlet>
					{DvcPageRoutes}

					<Route>
						<Redirect to={RouterMap.overview} />
					</Route>
				</IonRouterOutlet>
			</IonContent>
			<NavBar logout={() => { setUser(null); setPass(null); setDvc(null); } } selDevice={() => setDvc(null)} />
			</AlarmCtxProvider>
			</DeviceConnectionCTX.Provider>
			</RefreshContext.Provider>
		</>
	}

  return <IonApp>
		<Language.Provider value={lang}>
			<IonReactRouter>
				{content}
			</IonReactRouter>
		</Language.Provider>
	</IonApp>
}

export default App;

